/**
 * AdminRefundCompliance — admin console tab.
 * Lists all refund claims, opens each into a drawer showing docs,
 * video witness, no-claims cert, timeline; admins advance steps and
 * take a final decision.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FileText, Video, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STEP_STATUSES = ["pending", "in_review", "done", "failed"];

function fmtInr(n) {
  const v = Number(n || 0);
  return `INR ${v.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function StatusPill({ status }) {
  const map = {
    draft:       ["#C9A961", "Draft"],
    submitted:   ["#2FB999", "Submitted"],
    under_review:["#F5D67A", "Under Review"],
    approved:    ["#2FB999", "Approved"],
    rejected:    ["#C0392B", "Rejected"],
  };
  const [c, l] = map[status] || ["#8A8A82", status];
  return <span className="inline-block font-grotesk text-[10px] uppercase tracking-[0.28em] px-2 py-0.5 rounded-full border" style={{ color: c, borderColor: `${c}55` }}>{l}</span>;
}

function ClaimDetail({ claim, api, onChange }) {
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState("");
  const [decision, setDecision] = useState({ decision: "approved", reason: "", payout_amount: "" });

  const terminal = claim.status === "approved" || claim.status === "rejected";

  const advance = async (key, status) => {
    setBusy(key + status);
    try {
      await api.post(`/admin/refund-compliance/claim/${claim.id}/advance-step`, { step: key, status, note });
      toast.success("Step updated.");
      setNote("");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setBusy(null); }
  };

  const decide = async () => {
    setBusy("decision");
    try {
      await api.post(`/admin/refund-compliance/claim/${claim.id}/decision`, {
        decision: decision.decision,
        reason: decision.reason,
        payout_amount: decision.decision === "approved" ? Number(decision.payout_amount || 0) : null,
      });
      toast.success(`Claim ${decision.decision}.`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setBusy(null); }
  };

  const docUrl = (slot) => `${process.env.REACT_APP_BACKEND_URL}/api/refund-compliance/claim/${claim.id}/document/${slot}`;
  const videoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/refund-compliance/claim/${claim.id}/video`;
  const noClaimsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/refund-compliance/claim/${claim.id}/no-claims-cert.pdf`;

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-black/50 p-5 mt-3" data-testid={`admin-claim-detail-${claim.id}`}>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">Buyer</div>
          <div className="font-serif text-lg mt-1">{claim.business_name || claim.buyer_email}</div>
          <div className="font-body text-xs text-[#8A8A82]">{claim.buyer_email} · Vinci Code {claim.vinci_code || "—"}</div>
          <div className="mt-3 space-y-1 font-body text-[13px] text-[#C9C9C2]">
            <div><b className="text-[#8A8A82]">Order:</b> #{String(claim.order_id).slice(0, 8).toUpperCase()}</div>
            <div><b className="text-[#8A8A82]">Reason:</b> {claim.reason_category} — {claim.reason_summary}</div>
            <div><b className="text-[#8A8A82]">Amount:</b> {fmtInr(claim.disputed_amount)}</div>
            <div><b className="text-[#8A8A82]">Submitted:</b> {claim.submitted_at?.slice(0, 19) || "—"}</div>
            <div><b className="text-[#8A8A82]">Verified:</b> Email {claim.email_verified ? "✓" : "✗"} · <span title="SMS OTP discontinued — Zoho email is the verifier">SMS —</span></div>
          </div>
        </div>
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">Documents ({Object.keys(claim.documents || {}).length}/10)</div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {Object.keys(claim.documents || {}).map((slot) => (
              <a key={slot} href={docUrl(slot)} target="_blank" rel="noreferrer"
                className="rounded-sm border border-[#C9A961]/20 hover:border-[#C9A961]/50 px-3 py-2 font-body text-[11px] text-[#F5F5F0] flex items-center gap-2"
                data-testid={`admin-doc-${slot}`}>
                <FileText size={12} className="text-[#C9A961]" />{slot}
              </a>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {claim.video_witness && (
              <a href={videoUrl} target="_blank" rel="noreferrer" data-testid="admin-video-link"
                className="rounded-sm border border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B]/10 px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.28em]">
                <Video size={12} className="inline mr-1 -mt-0.5" />Play witness
              </a>
            )}
            {claim.no_claims_cert && (
              <a href={noClaimsUrl} target="_blank" rel="noreferrer" data-testid="admin-noclaims-link"
                className="rounded-sm border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961]/10 px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.28em]">
                <FileText size={12} className="inline mr-1 -mt-0.5" />No-Claims Cert
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-2">Timeline</div>
        <div className="space-y-2">
          {(claim.timeline || []).map((s) => (
            <div key={s.key} data-testid={`admin-step-${s.key}`}
              className="flex flex-wrap items-center gap-3 justify-between rounded-sm border border-[#C9A961]/10 bg-black/40 px-3 py-2">
              <div>
                <div className="font-body text-[13px] text-[#F5F5F0]">{s.label}</div>
                <div className="font-body text-[10px] text-[#8A8A82]">
                  {s.status} {s.completed_at ? "· " + s.completed_at.slice(0, 19) : ""}
                  {s.note ? " · " + s.note : ""}
                </div>
              </div>
              <div className="flex gap-1">
                {STEP_STATUSES.map((st) => (
                  <button key={st} onClick={() => advance(s.key, st)} disabled={busy === s.key + st || terminal}
                    data-testid={`admin-step-${s.key}-${st}`}
                    className={`rounded-sm px-2 py-1 font-grotesk text-[9px] uppercase tracking-[0.25em] border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${s.status === st ? "border-[#C9A961] text-[#C9A961]" : "border-white/10 text-[#8A8A82] hover:border-white/30"}`}>
                    {st === "in_review" ? "review" : st}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note for the next step change"
          className="w-full mt-3 bg-black border border-[#C9A961]/20 rounded-sm px-3 py-2 font-body text-[12px] text-[#F5F5F0]"
          data-testid="admin-step-note" />
      </div>

      {claim.status !== "approved" && claim.status !== "rejected" ? (
        <div className="mt-6 rounded-sm border border-[#C0392B]/40 bg-[#C0392B]/[0.06] p-4">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C0392B]">Final decision</div>
          <div className="grid md:grid-cols-3 gap-3 mt-3">
            <select value={decision.decision} onChange={(e) => setDecision({ ...decision, decision: e.target.value })}
              data-testid="admin-decision-select"
              className="bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm">
              <option value="approved">Approve</option>
              <option value="rejected">Reject</option>
            </select>
            {decision.decision === "approved" && (
              <input type="number" placeholder="Payout amount (INR)" value={decision.payout_amount}
                data-testid="admin-payout-amount"
                onChange={(e) => setDecision({ ...decision, payout_amount: e.target.value })}
                className="bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm" />
            )}
            <input placeholder="Reason (required for reject, optional for approve)" value={decision.reason}
              data-testid="admin-decision-reason"
              onChange={(e) => setDecision({ ...decision, reason: e.target.value })}
              className="bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm md:col-span-1" />
          </div>
          <button onClick={decide} disabled={busy === "decision" || (decision.decision === "rejected" && !decision.reason)}
            data-testid="admin-final-decision-btn"
            className="mt-3 rounded-sm bg-[#C9A961] hover:bg-[#F5D67A] text-black px-4 py-2 font-grotesk text-[11px] uppercase tracking-[0.28em] disabled:opacity-40 transition-colors">
            {decision.decision === "approved" ? <CheckCircle2 size={13} className="inline mr-1 -mt-0.5" /> : <XCircle size={13} className="inline mr-1 -mt-0.5" />}
            Commit decision
          </button>
        </div>
      ) : (
        <div className="mt-6 rounded-sm border border-white/10 bg-black/40 px-4 py-3 font-body text-[13px] text-[#8A8A82]" data-testid="admin-terminal-notice">
          Claim is <b className="text-[#F5F5F0]">{claim.status.toUpperCase()}</b> — the timeline and decision panel are locked. Open a fresh claim on a different order to make further changes.
        </div>
      )}
    </div>
  );
}

export function AdminRefundCompliance() {
  const { api } = useAuth();
  const [claims, setClaims] = useState([]);
  const [open, setOpen] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/refund-compliance/claims" + (filter ? `?status=${filter}` : ""));
      setClaims(data.claims || []);
    } catch (e) {
      const msg = e?.response?.status === 403 ? "Admins only." :
                    e?.response?.data?.detail || `HTTP ${e?.response?.status || "?"} — could not load claims.`;
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  }, [api, filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-refund-compliance">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Refund Compliance Desk</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0]">All buyer claims — timeline control</h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-claims-filter"
            className="bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm w-44">
            <option value="">All</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={load} data-testid="admin-claims-refresh"
            className="rounded-sm border border-[#C9A961]/40 text-[#C9A961] px-3 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] whitespace-nowrap">
            <RefreshCw size={12} className="inline mr-1 -mt-0.5" />Refresh
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="admin-claims-error"
          className="rounded-sm border border-[#C0392B]/50 bg-[#C0392B]/[0.08] text-[#F5F5F0] px-4 py-3 mb-4 font-body text-sm flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-[#C0392B]" />{error}
        </div>
      )}

      {loading ? <div className="font-body text-sm text-[#8A8A82]">Loading…</div> :
       claims.length === 0 && !error ? <div className="font-body text-sm text-[#8A8A82]">No claims found.</div> :
       claims.map((c) => (
        <div key={c.id} className="mb-3">
          <button onClick={() => setOpen({ ...open, [c.id]: !open[c.id] })} data-testid={`admin-claim-row-${c.id}`}
            className="w-full flex flex-wrap items-center justify-between gap-3 rounded-sm border border-[#C9A961]/20 hover:border-[#C9A961]/40 bg-black/50 p-4 text-left transition-colors">
            <div>
              <div className="font-serif text-[#F5F5F0]">#{c.id.slice(0, 8).toUpperCase()} · Order #{String(c.order_id).slice(0, 8).toUpperCase()}</div>
              <div className="font-body text-[11px] text-[#8A8A82] mt-1">
                {c.business_name || c.buyer_email} · {fmtInr(c.disputed_amount)} · {c.reason_category}
              </div>
            </div>
            <StatusPill status={c.status} />
          </button>
          {open[c.id] && <ClaimDetail claim={c} api={api} onChange={load} />}
        </div>
      ))}
    </div>
  );
}
